import { useState, useEffect } from "react";
import { X, Download, Share } from "lucide-react";
import { Button } from "./ui/button";

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    // Don't show if dismissed recently (7 days)
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Detect iOS
    const ua = window.navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      // Show iOS guide after 3 seconds
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  // iOS: show guide
  if (isIOS) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom" data-testid="pwa-install-banner">
        <div className="max-w-md mx-auto bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl shadow-black/50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="FitTrack Coach" className="w-12 h-12 rounded-xl" />
              <div>
                <p className="font-heading font-bold text-sm uppercase tracking-wide text-foreground">Install FitTrack Coach</p>
                <p className="text-xs text-muted-foreground mt-0.5">App jaisi experience paao</p>
              </div>
            </div>
            <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground p-1" data-testid="pwa-dismiss-btn">
              <X className="w-4 h-4" />
            </button>
          </div>

          {!showIOSGuide ? (
            <Button
              onClick={() => setShowIOSGuide(true)}
              className="w-full mt-3 h-11 btn-premium text-white font-bold text-sm uppercase tracking-wide rounded-xl"
              data-testid="pwa-install-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          ) : (
            <div className="mt-3 p-3 bg-white/[0.03] rounded-xl space-y-2.5" data-testid="pwa-ios-guide">
              <p className="text-xs font-bold text-foreground">3 simple steps:</p>
              <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                <span className="w-5 h-5 rounded-md bg-primary/20 text-primary flex items-center justify-center font-bold text-[10px] shrink-0">1</span>
                <span>Tap the <Share className="w-3.5 h-3.5 inline text-primary mx-0.5" /> Share button (bottom bar)</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                <span className="w-5 h-5 rounded-md bg-primary/20 text-primary flex items-center justify-center font-bold text-[10px] shrink-0">2</span>
                <span>Scroll down, tap <strong className="text-foreground">"Add to Home Screen"</strong></span>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                <span className="w-5 h-5 rounded-md bg-primary/20 text-primary flex items-center justify-center font-bold text-[10px] shrink-0">3</span>
                <span>Tap <strong className="text-foreground">"Add"</strong> — Done!</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Android/Chrome: show install button
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom" data-testid="pwa-install-banner">
      <div className="max-w-md mx-auto bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl shadow-black/50">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="FitTrack Coach" className="w-12 h-12 rounded-xl" />
            <div>
              <p className="font-heading font-bold text-sm uppercase tracking-wide text-foreground">Install FitTrack Coach</p>
              <p className="text-xs text-muted-foreground mt-0.5">Faster access, app jaisi feel</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground p-1" data-testid="pwa-dismiss-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Button
          onClick={handleInstall}
          className="w-full mt-3 h-11 btn-premium text-white font-bold text-sm uppercase tracking-wide rounded-xl"
          data-testid="pwa-install-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Install App
        </Button>
      </div>
    </div>
  );
}
